import { emptyRules, type Rules } from '@/api/teamCategories';

// Preset definitions for the "Quick start" chips on the category rules
// dialog. Picking one fills every field with sensible Nordic-sports
// defaults; the user can then tweak any individual value before saving.
//
// All values are saved with 'target' strictness so the AI treats them
// as soft guidance — clubs that want must-respect ceilings can bump the
// strictness manually after applying.

export type PresetId = 'knattar' | 'ungdom' | 'senior' | 'veteran';

// Mon-Fri weekday mask used by every preset. Position 0 = Mon … 6 = Sun.
const monToFri: Rules['allowed_weekdays'] = [
  true, true, true, true, true, false, false,
];

function preset(partial: Partial<Rules>): Rules {
  return { ...emptyRules(), ...partial };
}

export const RULE_PRESETS: { id: PresetId; rules: Rules }[] = [
  {
    id: 'knattar',
    rules: preset({
      sessions_per_week_target: 1,
      sessions_per_week_min: 1,
      sessions_per_week_max: 2,
      sessions_per_week_strictness: 'target',
      session_duration_minutes_target: 60,
      session_duration_strictness: 'target',
      time_window_start: '17:00',
      time_window_end: '19:00',
      time_window_strictness: 'target',
      allowed_weekdays: monToFri,
      allowed_weekdays_strictness: 'target',
      preferred_location_fraction_denominator: 4,
      min_location_fraction_denominator: 8,
      location_fraction_strictness: 'target',
    }),
  },
  {
    id: 'ungdom',
    rules: preset({
      sessions_per_week_target: 3,
      sessions_per_week_min: 3,
      sessions_per_week_max: 4,
      sessions_per_week_strictness: 'target',
      session_duration_minutes_target: 75,
      session_duration_minutes_min: 60,
      session_duration_minutes_max: 90,
      session_duration_strictness: 'target',
      time_window_start: '17:00',
      time_window_end: '20:00',
      time_window_strictness: 'target',
      allowed_weekdays: monToFri,
      allowed_weekdays_strictness: 'target',
      preferred_location_fraction_denominator: 1,
      min_location_fraction_denominator: 2,
      location_fraction_strictness: 'target',
    }),
  },
  {
    id: 'senior',
    rules: preset({
      sessions_per_week_target: 5,
      sessions_per_week_min: 5,
      sessions_per_week_max: 6,
      sessions_per_week_strictness: 'target',
      session_duration_minutes_target: 105,
      session_duration_minutes_min: 90,
      session_duration_minutes_max: 120,
      session_duration_strictness: 'target',
      time_window_start: '17:00',
      time_window_end: '23:00',
      time_window_strictness: 'target',
      allowed_weekdays: monToFri,
      allowed_weekdays_strictness: 'target',
      preferred_location_fraction_denominator: 1,
      min_location_fraction_denominator: 1,
      location_fraction_strictness: 'target',
    }),
  },
  {
    id: 'veteran',
    rules: preset({
      sessions_per_week_target: 2,
      sessions_per_week_min: 2,
      sessions_per_week_max: 3,
      sessions_per_week_strictness: 'target',
      session_duration_minutes_target: 75,
      session_duration_minutes_min: 60,
      session_duration_minutes_max: 90,
      session_duration_strictness: 'target',
      time_window_start: '19:00',
      time_window_end: '23:00',
      time_window_strictness: 'target',
      allowed_weekdays: monToFri,
      allowed_weekdays_strictness: 'target',
      preferred_location_fraction_denominator: 1,
      min_location_fraction_denominator: 2,
      location_fraction_strictness: 'target',
    }),
  },
];

// activePresetId returns the id of the preset whose rule values match the
// current form exactly, or null if the form is empty / customized. Used to
// give the preset chips an "active" visual state — without this, clicking a
// chip on a fresh category produces no on-chip feedback (the form below
// updates but the chip itself looks unchanged), which reads as "nothing
// happened" until the user clicks a second time and the overwrite confirm
// pops. updated_at is excluded because the server stamps it independently.
export function activePresetId(r: Rules): PresetId | null {
  const key = rulesKey(r);
  for (const p of RULE_PRESETS) {
    if (rulesKey(p.rules) === key) return p.id;
  }
  return null;
}

function rulesKey(r: Rules): string {
  const { updated_at: _updated_at, ...rest } = r;
  return JSON.stringify(rest);
}

// hasAnyValueSet returns true when at least one user-visible field on the
// rules form has a non-empty value. Used to decide whether picking a preset
// needs the "overwrite existing values?" confirmation step.
export function hasAnyValueSet(r: Rules): boolean {
  return (
    r.sessions_per_week_target !== null ||
    r.sessions_per_week_min !== null ||
    r.sessions_per_week_max !== null ||
    r.session_duration_minutes_target !== null ||
    r.session_duration_minutes_min !== null ||
    r.session_duration_minutes_max !== null ||
    r.time_window_start !== null ||
    r.time_window_end !== null ||
    r.allowed_weekdays !== null ||
    r.preferred_location_fraction_denominator !== null ||
    r.min_location_fraction_denominator !== null ||
    r.min_leaders_present !== null ||
    r.head_coach_required !== null
  );
}
